import api from '@/shared/utils/apiConfig';
import i18n from '@/shared/i18n';
import { formatPersonName } from '@/shared/utils/personName';

export type LeadStatus =
  | 'new'
  | 'in_progress'
  | 'appointment_booked'
  | 'unqualified'
  | 'won'
  | 'lost';

export type LeadLostReason =
  | 'price'
  | 'distance'
  | 'chose_other'
  | 'no_response'
  | 'spam'
  | 'other';

export type LeadTreatmentInterest =
  | 'scaling'
  | 'implant'
  | 'general_exam'
  | 'pediatric'
  | 'other'
  | 'ortho'
  | 'ceramic'
  | 'whitening';

export interface LeadStaffSummary {
  id: string;
  firstName: string;
  lastName: string;
  staffCode?: string;
}

export interface LeadClinicSummary {
  id: string;
  name: string;
  branchCode?: string;
  shortName?: string;
}

export interface LeadUserSummary {
  id: string;
  name: string;
}

export interface LeadPatientSummary {
  id: string;
  name?: string;
  patientId?: string;
}

export interface LeadCustomerSourceSummary {
  id: string;
  name: string;
  code: string;
}

export type LeadContactChannel =
  | 'phone'
  | 'zalo'
  | 'sms'
  | 'email'
  | 'in_person'
  | 'other';

export type LeadContactOutcome =
  | 'reached'
  | 'no_answer'
  | 'callback'
  | 'wrong_number'
  | 'interested'
  | 'not_interested'
  | 'other';

export interface LeadContactCall {
  callUuid: string;
  direction?: 'inbound' | 'outbound' | 'local';
  status: 'ringing' | 'answered' | 'hangup' | 'missed';
  sipUser?: string;
  durationSec?: number;
  recordingUrl?: string;
  phoneNumber?: string;
}

export interface LeadContactActivity {
  id: string;
  source?: 'manual' | 'omicall';
  channel: LeadContactChannel;
  outcome: LeadContactOutcome;
  text?: string;
  contactStaffId?: string;
  contactStaff?: LeadStaffSummary;
  contactedAt?: string;
  callbackDueAt?: string;
  createdBy: LeadUserSummary;
  createdAt: string;
  call?: LeadContactCall;
}

export interface AddLeadContactActivityPayload {
  channel: LeadContactChannel;
  outcome: LeadContactOutcome;
  text?: string;
  contactStaffId: string;
  contactedAt: string;
  callbackDueAt?: string;
}

export interface LeadCallbackTaskResult {
  created: boolean;
  id?: string;
  reason?: 'no_permission' | 'failed';
}

export interface LeadStatusHistoryEntry {
  id: string;
  from?: LeadStatus;
  to: LeadStatus;
  at: string;
  by: LeadUserSummary;
}

export interface LeadReferredBy {
  id: string;
  kind: 'patient' | 'staff' | 'external';
  name: string;
  code: string;
  phone: string;
  patientId?: string;
  staffId?: string;
}

export interface LeadAdAttribution {
  adId: string;
  adName?: string;
  adsetId?: string;
  adsetName?: string;
  campaignId?: string;
  campaignName?: string;
  insertedAt?: string;
}

export interface Lead {
  id: string;
  firstName: string;
  lastName?: string;
  name: string;
  leadCode?: string;
  phone?: string;
  email?: string;
  gender?: 'male' | 'female' | 'other';
  status: LeadStatus;
  lostReason?: LeadLostReason;
  customerSource?: LeadCustomerSourceSummary;
  referredBy?: LeadReferredBy;
  treatmentInterest?: LeadTreatmentInterest;
  assigneeId?: string;
  assignee?: LeadStaffSummary;
  clinicId?: string;
  clinic?: LeadClinicSummary;
  note?: string;
  adAttributions?: LeadAdAttribution[];
  contactActivities: LeadContactActivity[];
  statusHistory: LeadStatusHistoryEntry[];
  convertedPatientId?: string;
  convertedPatient?: LeadPatientSummary;
  convertedAt?: string;
  createdBy: LeadUserSummary;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeadPayload {
  firstName: string;
  lastName?: string;
  phone?: string;
  email?: string;
  gender?: 'male' | 'female' | 'other';
  clinicId?: string;
  customerSource: string;
  referredBy?:
    | { referrer: string }
    | { patient: string }
    | { staff: string };
  treatmentInterest?: LeadTreatmentInterest;
  assigneeId?: string;
  note?: string;
}

export interface UpdateLeadPayload {
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
  gender?: 'male' | 'female' | 'other' | null;
  clinicId?: string | null;
  customerSource?: string;
  referredBy?:
    | { referrer: string }
    | { patient: string }
    | { staff: string }
    | null;
  treatmentInterest?: LeadTreatmentInterest | null;
  assigneeId?: string | null;
  note?: string | null;
}

export interface ConvertLeadPayload {
  gender?: 'male' | 'female' | 'other';
  dateOfBirth?: string;
  registeredClinic?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  email?: string;
}

export interface LeadListParams {
  status?: LeadStatus | string;
  assigneeId?: string;
  clinicId?: string;
  customerSource?: string;
  treatmentInterest?: string;
  search?: string;
  page?: number;
  limit?: number;
  sort?: string;
}

interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  count?: number;
  total?: number;
  page?: number;
  pages?: number;
  message?: string;
}

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  get new() { return i18n.t('lead:status.new'); },
  get in_progress() { return i18n.t('lead:status.in_progress'); },
  get appointment_booked() { return i18n.t('lead:status.appointment_booked'); },
  get unqualified() { return i18n.t('lead:status.unqualified'); },
  get won() { return i18n.t('lead:status.won'); },
  get lost() { return i18n.t('lead:status.lost'); },
};

export const LEAD_LOST_REASON_LABELS: Record<LeadLostReason, string> = {
  get price() { return i18n.t('lead:lostReason.price'); },
  get distance() { return i18n.t('lead:lostReason.distance'); },
  get chose_other() { return i18n.t('lead:lostReason.chose_other'); },
  get no_response() { return i18n.t('lead:lostReason.no_response'); },
  get spam() { return i18n.t('lead:lostReason.spam'); },
  get other() { return i18n.t('lead:lostReason.other'); },
};

export const LEAD_TREATMENT_LABELS: Record<LeadTreatmentInterest, string> = {
  get scaling() { return i18n.t('lead:treatmentInterest.scaling'); },
  get implant() { return i18n.t('lead:treatmentInterest.implant'); },
  get general_exam() { return i18n.t('lead:treatmentInterest.general_exam'); },
  get pediatric() { return i18n.t('lead:treatmentInterest.pediatric'); },
  get other() { return i18n.t('lead:treatmentInterest.other'); },
  get ortho() { return i18n.t('lead:treatmentInterest.ortho'); },
  get ceramic() { return i18n.t('lead:treatmentInterest.ceramic'); },
  get whitening() { return i18n.t('lead:treatmentInterest.whitening'); },
};

export const LEAD_CHANNEL_LABELS: Record<LeadContactChannel, string> = {
  get phone() { return i18n.t('lead:channel.phone'); },
  get zalo() { return i18n.t('lead:channel.zalo'); },
  get sms() { return i18n.t('lead:channel.sms'); },
  get email() { return i18n.t('lead:channel.email'); },
  get in_person() { return i18n.t('lead:channel.in_person'); },
  get other() { return i18n.t('lead:channel.other'); },
};

export const LEAD_OUTCOME_LABELS: Record<LeadContactOutcome, string> = {
  get reached() { return i18n.t('lead:outcome.reached'); },
  get no_answer() { return i18n.t('lead:outcome.no_answer'); },
  get callback() { return i18n.t('lead:outcome.callback'); },
  get wrong_number() { return i18n.t('lead:outcome.wrong_number'); },
  get interested() { return i18n.t('lead:outcome.interested'); },
  get not_interested() { return i18n.t('lead:outcome.not_interested'); },
  get other() { return i18n.t('lead:outcome.other'); },
};

export const formatLeadStaffName = (staff?: LeadStaffSummary | null): string => {
  if (!staff) return '—';
  const name = formatPersonName(staff);
  return staff.staffCode ? `${name} (${staff.staffCode})` : name || '—';
};

export const formatLeadCustomerSource = (
  source?: LeadCustomerSourceSummary | string | null
): string => {
  if (!source) return '—';
  if (typeof source === 'object') {
    return source.name || source.code || '—';
  }
  return String(source);
};

export const leadAPI = {
  getAll: async (
    params: LeadListParams = {},
    options?: { signal?: AbortSignal }
  ): Promise<ApiResponse<Lead[]>> => {
    const response = await api.get('/leads', {
      params,
      signal: options?.signal,
    });
    return response.data;
  },

  getById: async (id: string): Promise<ApiResponse<Lead>> => {
    const response = await api.get(`/leads/${id}`);
    return response.data;
  },

  create: async (payload: CreateLeadPayload): Promise<ApiResponse<Lead>> => {
    const response = await api.post('/leads', payload);
    return response.data;
  },

  update: async (id: string, payload: UpdateLeadPayload): Promise<ApiResponse<Lead>> => {
    const response = await api.put(`/leads/${id}`, payload);
    return response.data;
  },

  updateStatus: async (
    id: string,
    payload: { status: LeadStatus; lostReason?: LeadLostReason }
  ): Promise<ApiResponse<Lead>> => {
    const response = await api.patch(`/leads/${id}/status`, payload);
    return response.data;
  },

  addContactActivity: async (
    id: string,
    payload: AddLeadContactActivityPayload
  ): Promise<ApiResponse<Lead> & { callbackTask?: LeadCallbackTaskResult }> => {
    const response = await api.post(`/leads/${id}/contact-activities`, payload);
    return response.data;
  },

  convert: async (
    id: string,
    payload: ConvertLeadPayload | Record<string, unknown>
  ): Promise<ApiResponse<{ lead: Lead; patient: { id: string; name: string; patientId?: string } }>> => {
    const response = await api.post(`/leads/${id}/convert`, payload);
    return response.data;
  },

  delete: async (id: string): Promise<ApiResponse<Record<string, never>>> => {
    const response = await api.delete(`/leads/${id}`);
    return response.data;
  },
};
